/**
 * SandboxExercise - Free practice mode with specific trading tools enabled.
 */

import { memo } from 'react'
import { PlayCircle, Info } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { SandboxContent, SandboxTool } from '../../curriculum/manager'

export interface SandboxExerciseProps {
  /** The sandbox content configuration */
  content: SandboxContent
  /** Optional className */
  className?: string
}

const toolLabels: Record<SandboxTool, { name: string; description: string }> = {
  'market-order': {
    name: 'Market Orders',
    description: 'Buy or sell immediately at the best available price',
  },
  'limit-order': {
    name: 'Limit Orders',
    description: 'Set your own price and wait for a match',
  },
  'cancel-order': {
    name: 'Cancel Orders',
    description: 'Remove your pending orders from the book',
  },
  'speed-control': {
    name: 'Speed Control',
    description: 'Adjust simulation speed or pause',
  },
}

/**
 * SandboxExercise displays a free practice area with enabled tools.
 */
export const SandboxExercise = memo(function SandboxExercise({
  content,
  className,
}: SandboxExerciseProps) {
  const { tools } = content

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <PlayCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
          <div>
            <h3 className="font-medium text-foreground">Practice Mode</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              This is a safe space to practice trading. Try out the tools below
              using the trading panel on the right. Your actions won't affect
              your real progress.
            </p>
          </div>
        </div>
      </div>

      {/* Available tools */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-foreground">
            Available Tools
          </h4>
        </div>

        <div className="space-y-2">
          {tools.map((tool) => {
            const toolInfo = toolLabels[tool]
            return (
              <div
                key={tool}
                className="flex items-start gap-3 rounded-md bg-muted/50 p-3"
              >
                <div className="mt-0.5 h-2 w-2 rounded-full bg-green-500" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {toolInfo.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {toolInfo.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Instructions */}
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <h4 className="mb-2 text-sm font-medium text-foreground">
          How to Practice
        </h4>
        <ol className="space-y-1 text-sm text-muted-foreground">
          <li>1. Look at the Order Book on the right to see current prices</li>
          <li>2. Use the Trading Panel to place orders</li>
          <li>3. Watch how your orders affect the market</li>
          <li>4. When you're ready, click Next to continue</li>
        </ol>
      </div>
    </div>
  )
})
